import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, MenuItem, Paper, TextField, Typography,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { abrirPapeletaPdf, dinero, emitirPlanilla, prepararPapeleta } from "../services/planillasService";
import { fieldSx } from "../../egresos/components/formStyles";

const SIGLA_CIUDAD = { "La Paz": "LPZ", "Santa Cruz": "SCZ", Cochabamba: "CBBA" };
const n = (v) => { const x = Number(String(v ?? "").replace(/,/g, "")); return Number.isFinite(x) ? x : 0; };

const Fila = ({ etiqueta, valor, negrita }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.25 }}>
    <Typography sx={{ fontSize: 12.5, fontWeight: negrita ? 800 : 400 }}>{etiqueta}</Typography>
    <Typography sx={{ fontSize: 12.5, fontWeight: negrita ? 800 : 400 }}>{valor}</Typography>
  </Box>
);

// Papeleta de pago de un empleado (antes busqueda.php + busqueda1.php): el cálculo se ve en vivo y el servidor lo vuelve a calcular al guardar.
export default function PapeletaDialog({ empleado, onClose, onEmitida }) {
  const [prep, setPrep] = useState(null);
  const [f, setF] = useState(null);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    let activo = true;
    prepararPapeleta(empleado.id)
      .then(({ data }) => {
        if (!activo) return;
        setPrep(data);
        const sigla = SIGLA_CIUDAD[data.empleado.ciudad];
        const concepto = data.conceptos.find((c) => sigla && c.concepto.includes(sigla)) || data.conceptos.find((c) => /Salario/i.test(c.concepto)) || data.conceptos[0];
        setF({
          mes: data.meses[new Date().getMonth()], year: String(new Date().getFullYear()), num_cheque: "", banco: data.bancos[0]?.nombre || "", concepto_id: concepto?.id ?? "",
          servicio: "General", observacion: "", extras: "0", otro_bono: "0", otro_descuento: "0", bono_esp: "0", texto_bono: "Bono por incremento:",
          id_anticipo: "", descuento_anticipo: "0",
        });
      })
      .catch((e) => activo && setError(e?.response?.data?.error || "No se pudo preparar la papeleta."));
    return () => { activo = false; };
  }, [empleado.id]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const calc = useMemo(() => {
    if (!prep || !f) return null;
    const e = prep.empleado;
    const totalGanado = n(e.sueldo_basico) + n(f.extras) + n(f.otro_bono) + prep.antiguedad.bono;
    const descuentos = prep.descuentos.map((d) => ({ ...d, monto: (totalGanado * d.porcentaje) / 100 }));
    const sumaLey = descuentos.reduce((a, d) => a + d.monto, 0);
    const anticipo = f.id_anticipo ? n(f.descuento_anticipo) : 0;
    const totalDescuentos = sumaLey + anticipo + n(f.otro_descuento);
    const bonoEspecial = n(e.incremento) + n(f.bono_esp);
    return { totalGanado, descuentos, totalDescuentos, bonoEspecial, liquido: totalGanado + bonoEspecial - totalDescuentos, anticipo };
  }, [prep, f]);

  const anticipoElegido = prep?.anticipos.find((a) => a.id === Number(f?.id_anticipo));

  const emitir = async () => {
    setGuardando(true);
    setError("");
    try {
      const { data } = await emitirPlanilla({
        id_empleado: empleado.id, mes: f.mes, year: f.year, num_cheque: f.num_cheque, banco: f.banco, concepto_id: f.concepto_id, servicio: f.servicio,
        observacion: f.observacion, extras: f.extras, otro_bono: f.otro_bono, otro_descuento: f.otro_descuento, bono_esp: f.bono_esp, texto_bono: f.texto_bono,
        id_anticipo: f.id_anticipo || null, descuento_anticipo: f.id_anticipo ? f.descuento_anticipo : 0,
      });
      setResultado(data);
      onEmitida?.(data);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo emitir la planilla.");
    } finally {
      setGuardando(false);
    }
  };

  const campo = (k, label, size, extra = {}) => (
    <Grid size={size}><TextField fullWidth size="small" label={label} value={f[k]} onChange={set(k)} sx={fieldSx} {...extra} /></Grid>
  );
  const select = (k, label, opciones, size) => (
    <Grid size={size}>
      <TextField select fullWidth size="small" label={label} value={f[k]} onChange={set(k)} sx={fieldSx}>
        {opciones.map((o) => <MenuItem key={o.v} value={o.v}>{o.t}</MenuItem>)}
      </TextField>
    </Grid>
  );

  if (resultado) {
    return (
      <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 800 }}>Planilla emitida</DialogTitle>
        <DialogContent dividers>
          <Alert severity="success" sx={{ mb: 1 }}>Se registró el pago de {empleado.nombre_completo} por <strong>Bs. {dinero(resultado.liquido)}</strong> y el cheque en Egresos.</Alert>
          {resultado.vacacion_dias > 0 && <Alert severity="info" sx={{ py: 0 }}>Se sumaron {resultado.vacacion_dias} días a sus vacaciones.</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 0.75 }}>
          <Button size="small" onClick={onClose}>Cerrar</Button>
          <Button size="small" variant="contained" startIcon={<PictureAsPdfIcon />} onClick={() => abrirPapeletaPdf(resultado.id_pago).catch(() => setError("No se pudo generar la papeleta."))}>Imprimir papeleta</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const e = prep?.empleado;
  return (
    <Dialog open onClose={() => !guardando && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 800 }}>Papeleta de pago — {empleado.nombre_completo}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
        {!prep && !error && <CircularProgress size={20} />}
        {prep && f && calc && (
          <>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
              {e.cargo} · {e.ciudad} · ingreso {e.fecha_ini} · {prep.antiguedad.anios} años de antigüedad · tipo de cambio {prep.tipo_cambio || "—"}
            </Typography>
            {prep.vacacion.aplica && <Alert severity="info" sx={{ py: 0, mb: 1 }}>Este mes se le sumarán <strong>{prep.vacacion.dias} días</strong> a sus vacaciones al emitir la planilla.</Alert>}
            {!prep.tipo_cambio && <Alert severity="warning" sx={{ py: 0, mb: 1 }}>No hay tipo de cambio registrado (tabla dólar): sin él no se puede emitir.</Alert>}

            <Grid container spacing={0.75}>
              {select("mes", "Correspondiente a", prep.meses.map((m) => ({ v: m, t: m })), { xs: 6, md: 3 })}
              {campo("year", "Año", { xs: 6, md: 2 }, { type: "number" })}
              {campo("num_cheque", "N° de cheque", { xs: 6, md: 3 })}
              {select("banco", "Banco", prep.bancos.map((b) => ({ v: b.nombre, t: b.nombre })), { xs: 6, md: 4 })}
              {select("concepto_id", "Concepto", prep.conceptos.map((c) => ({ v: c.id, t: c.concepto })), { xs: 12, md: 5 })}
              {select("servicio", "Tipo de servicio", prep.servicios.map((s) => ({ v: s, t: s })), { xs: 12, md: 3 })}
              {campo("observacion", "Observación", { xs: 12, md: 4 })}
            </Grid>

            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, mb: 0.5 }}>HABERES</Typography>
                  <Fila etiqueta="Sueldo básico" valor={dinero(e.sueldo_basico)} />
                  <Fila etiqueta={`Bono de antigüedad (${prep.antiguedad.porcentaje}%)`} valor={dinero(prep.antiguedad.bono)} />
                  <Grid container spacing={0.75} sx={{ my: 0.25 }}>
                    {campo("extras", "Horas extras", { xs: 6 }, { type: "number" })}
                    {campo("otro_bono", "Otros bonos", { xs: 6 }, { type: "number" })}
                  </Grid>
                  <Divider sx={{ my: 0.5 }} />
                  <Fila etiqueta="TOTAL GANADO Bs." valor={dinero(calc.totalGanado)} negrita />
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, mb: 0.5 }}>DESCUENTOS</Typography>
                  {calc.descuentos.map((d) => <Fila key={d.descripcion} etiqueta={`${d.descripcion} ${d.porcentaje}%`} valor={dinero(d.monto)} />)}
                  <Grid container spacing={0.75} sx={{ my: 0.25 }}>
                    {prep.anticipos.length > 0 && select("id_anticipo", "Descontar anticipo", [{ v: "", t: "(ninguno)" }, ...prep.anticipos.map((a) => ({ v: a.id, t: `${a.fecha_pago} · saldo Bs. ${dinero(a.saldo)}` }))], { xs: 7 })}
                    {prep.anticipos.length > 0 && campo("descuento_anticipo", "Monto", { xs: 5 }, { type: "number", disabled: !f.id_anticipo, helperText: anticipoElegido ? `máx. ${dinero(anticipoElegido.saldo)}` : undefined })}
                    {campo("otro_descuento", "Otros descuentos", { xs: 12 }, { type: "number" })}
                  </Grid>
                  <Divider sx={{ my: 0.5 }} />
                  <Fila etiqueta="TOTAL DESCUENTOS Bs." valor={dinero(calc.totalDescuentos)} negrita />
                </Paper>
              </Grid>
            </Grid>

            <Grid container spacing={0.75} sx={{ mt: 0.5, alignItems: "center" }}>
              {campo("texto_bono", "Texto del bono especial", { xs: 12, md: 5 })}
              {campo("bono_esp", "Bono especial (Bs)", { xs: 6, md: 3 }, { type: "number", helperText: n(e.incremento) ? `+ incremento de contrato ${dinero(e.incremento)}` : undefined })}
              <Grid size={{ xs: 6, md: 4 }}>
                <Typography sx={{ fontSize: 11, color: "text.secondary", textAlign: "right" }}>LÍQUIDO PAGABLE</Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 900, textAlign: "right", lineHeight: 1.1 }}>Bs. {dinero(calc.liquido)}</Typography>
                {prep.tipo_cambio > 0 && <Typography sx={{ fontSize: 11, color: "text.secondary", textAlign: "right" }}>≈ $us {dinero(calc.liquido / prep.tipo_cambio)}</Typography>}
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 0.75 }}>
        <Button size="small" onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button size="small" variant="contained" disabled={!calc || guardando || !prep?.tipo_cambio || !(calc.liquido > 0)} onClick={emitir}>
          {guardando ? <CircularProgress size={16} color="inherit" /> : "Emitir planilla"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
